import type { AlertSeverity, AlertCategory } from '@/types/alerts';

/**
 * Maps Amazon Connect alert eventName to fallback display information
 * IMPORTANT: Backend is the primary source of alert names and descriptions via SSE
 * This map should only be used as a fallback if backend doesn't provide name/description
 * Alert eventNames come from EventBridge events with detail-type = "Metrics Rules Matched"
 * Examples: CallVolumeSpikeEvent, AgentNotAvailableEvent
 */
export const ALERT_TYPE_MAP: Record<
  string,
  {
    displayName: string;
    category: AlertCategory;
    severity: AlertSeverity;
    description?: string;
  }
> = {
  // Real-time metric alerts from Amazon Connect
  // Backend sends name and description directly, these mappings are fallback only
  CallVolumeSpikeEvent: {
    displayName: 'Call Volume Spike',
    category: 'service_level_breach',
    severity: 'high',
  },
  CallVolumeSpikeAlert: {
    displayName: 'Call Volume Spike',
    category: 'service_level_breach',
    severity: 'high',
  },
  AgentNotAvailableEvent: {
    displayName: 'Agent Not Available',
    category: 'agent_offline',
    severity: 'critical',
  },
  AgentNotAvailableAlert: {
    displayName: 'Agent Not Available',
    category: 'agent_offline',
    severity: 'critical',
  },

  // Legacy/placeholder mappings for reference
  AgentOffline: {
    displayName: 'Agent Offline',
    category: 'agent_offline',
    severity: 'critical',
  },
  HandleTimeExceeded: {
    displayName: 'Handle Time Exceeded',
    category: 'handle_time_exceeded',
    severity: 'high',
  },
  ServiceLevelBreach: {
    displayName: 'Service Level Breach',
    category: 'service_level_breach',
    severity: 'critical',
  },
  MissedCalls: {
    displayName: 'Missed Calls',
    category: 'missed_calls',
    severity: 'high',
  },
  MISSED_CALL: {
    displayName: 'Missed Call',
    category: 'missed_calls',
    severity: 'high',
  },
  MISSED_CHAT: {
    displayName: 'Missed Chat',
    category: 'missed_calls',
    severity: 'high',
  },
  AdherenceViolation: {
    displayName: 'Adherence Violation',
    category: 'adherence_violation',
    severity: 'medium',
  },
};

/**
 * Get friendly display name for alert type
 */
export function getAlertTypeInfo(actionName: string) {
  return (
    ALERT_TYPE_MAP[actionName] || {
      displayName: actionName,
      category: 'other' as const,
      severity: 'low' as const,
    }
  );
}

/**
 * Format alert message with type information
 */
export function formatAlertMessage(actionName: string, customMessage?: string) {
  if (customMessage) {
    return customMessage;
  }

  const typeInfo = getAlertTypeInfo(actionName);
  return typeInfo.displayName;
}

/**
 * Get display name from alert event
 * Prefers backend 'name' field, falls back to 'message', then to type mapping
 */
export function getAlertDisplayName(alert: {
  name?: string;
  message?: string;
  type: string;
}): string {
  return (
    alert.name ||
    alert.message ||
    getAlertTypeInfo(alert.type).displayName ||
    'Alert'
  );
}

/**
 * Get description from alert event
 * Prefers backend 'description' field, falls back to type mapping
 */
export function getAlertDescription(alert: {
  description?: string;
  type: string;
}): string | undefined {
  return alert.description || getAlertTypeInfo(alert.type).description;
}

/**
 * Get agent display name based on event type
 * Only shows agent name for events that have agent information
 * Other events like NoAgentsOnCall don't have agent names, show "-" instead
 */
const NAME_EVENTS = ['AfterCallWork60s', 'MISSED_CALL', 'MISSED_CHAT'];

export function getAgentDisplayName(alert: {
  type?: string;
  agentName?: string;
}): string {
  if (NAME_EVENTS.includes(alert.type || '')) {
    return alert.agentName || '-';
  }
  return '-';
}
